import React, { useState } from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { useQuery, useMutation, useQueryClient } from "react-query";
import {
  FiPackage,
  FiSearch,
  FiFilter,
  FiCalendar,
  FiMapPin,
  FiPhone,
  FiUser,
  FiClock,
  FiCheckCircle,
  FiTruck,
  FiXCircle,
  FiAlertCircle,
  FiEye,
  FiEdit,
  FiRefreshCw,
  FiNavigation,
  FiX,
  FiMail,
  FiHash,
} from "react-icons/fi";
import { ordersAPI } from "../../services/api";
import LoadingSpinner from "../../components/LoadingSpinner";
import toast from "react-hot-toast";

const Container = styled.div`
  padding: 2rem;
  max-width: 1400px;
  margin: 0 auto;
`;

const Header = styled.div`
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  color: #2d3436;
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const Subtitle = styled.p`
  color: #636e72;
  font-size: 1.1rem;
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const StatCard = styled(motion.div)`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  border-left: 4px solid ${(props) => props.$color || "#2d5016"};
`;

const StatHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const StatIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${(props) => props.$bgColor || "#f8f9fa"};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${(props) => props.$color || "#2d5016"};
  font-size: 1.2rem;
`;

const StatLabel = styled.span`
  color: #636e72;
  font-size: 0.9rem;
  font-weight: 500;
`;

const StatValue = styled.div`
  color: #2d3436;
  font-size: 2rem;
  font-weight: 700;
  margin-top: 0.5rem;
`;

const FiltersContainer = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  margin-bottom: 2rem;
  display: flex;
  gap: 1rem;
  align-items: center;
  flex-wrap: wrap;
`;

const SearchInput = styled.input`
  flex: 1;
  min-width: 300px;
  padding: 0.75rem 1rem;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  font-size: 1rem;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: #2d5016;
  }
`;

const FilterSelect = styled.select`
  padding: 0.75rem 1rem;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  font-size: 1rem;
  background: white;
  cursor: pointer;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: #2d5016;
  }
`;

const DateInput = styled.input`
  padding: 0.75rem 1rem;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  font-size: 1rem;
  background: white;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: #2d5016;
  }
`;

const OrdersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: 1.5rem;
`;

const OrderCard = styled(motion.div)`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  border: 1px solid #e9ecef;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  }
`;

const OrderHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
`;

const OrderInfo = styled.div`
  flex: 1;
`;

const OrderId = styled.h3`
  color: #2d3436;
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 0.25rem;
`;

const CustomerName = styled.p`
  color: #636e72;
  font-size: 0.9rem;
  margin-bottom: 0.25rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const CustomerPhone = styled.p`
  color: #636e72;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const StatusBadge = styled.span`
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  &.pending {
    background: #fff3cd;
    color: #856404;
  }

  &.confirmed {
    background: #d1ecf1;
    color: #0c5460;
  }

  &.preparing {
    background: #f8d7da;
    color: #721c24;
  }

  &.out_for_delivery {
    background: #d4edda;
    color: #155724;
  }

  &.delivered {
    background: #e2e3e5;
    color: #383d41;
  }

  &.cancelled {
    background: #f8d7da;
    color: #721c24;
  }
`;

const MealInfo = styled.div`
  background: #f8f9fa;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1rem;
`;

const MealTitle = styled.h4`
  color: #2d3436;
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const MealDetails = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem;
  font-size: 0.9rem;
  color: #636e72;
`;

const DetailItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const DeliveryInfo = styled.div`
  background: #f0f8f0;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1rem;
`;

const DeliveryTitle = styled.h5`
  color: #2d5016;
  font-size: 0.9rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const DeliveryText = styled.p`
  color: #2d5016;
  font-size: 0.9rem;
  line-height: 1.4;
  margin-bottom: 0.5rem;
`;

const DeliveryTime = styled.p`
  color: #2d5016;
  font-size: 0.9rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const OrderActions = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 1rem;
`;

const ActionButton = styled.button`
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &.primary {
    background: #2d5016;
    color: white;

    &:hover {
      background: #1e3a0f;
    }
  }

  &.secondary {
    background: #f8f9fa;
    color: #636e72;
    border: 1px solid #dee2e6;

    &:hover {
      background: #e9ecef;
    }
  }

  &.success {
    background: #28a745;
    color: white;

    &:hover {
      background: #218838;
    }
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const StatusSelect = styled.select`
  padding: 0.5rem;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  font-size: 0.8rem;
  background: white;
  cursor: pointer;
  margin-right: 0.5rem;

  &:focus {
    outline: none;
    border-color: #2d5016;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: #636e72;

  svg {
    font-size: 4rem;
    margin-bottom: 1rem;
    opacity: 0.5;
  }

  h3 {
    font-size: 1.5rem;
    margin-bottom: 0.5rem;
    color: #2d3436;
  }

  p {
    font-size: 1.1rem;
    line-height: 1.6;
  }
`;

const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 2rem;
`;

const ModalContent = styled(motion.div)`
  background: white;
  border-radius: 16px;
  max-width: 800px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
`;

const ModalHeader = styled.div`
  padding: 2rem;
  border-bottom: 1px solid #e9ecef;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #2d5016;
  color: white;
  border-radius: 16px 16px 0 0;

  h2 {
    margin: 0;
    font-size: 1.5rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  button {
    background: transparent;
    border: none;
    color: white;
    font-size: 1.5rem;
    cursor: pointer;
    padding: 0.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    transition: background 0.3s ease;

    &:hover {
      background: rgba(255, 255, 255, 0.2);
    }
  }
`;

const ModalBody = styled.div`
  padding: 2rem;
`;

const DetailSection = styled.div`
  margin-bottom: 2rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h3`
  color: #2d5016;
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid #e9ecef;
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0.75rem 0;
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
  }
`;

const DetailLabel = styled.span`
  color: #636e72;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const DetailValue = styled.span`
  color: #2d3436;
  font-weight: 600;
  text-align: right;
`;

const StatusHistoryItem = styled.div`
  padding: 0.75rem;
  background: #f8f9fa;
  border-radius: 8px;
  margin-bottom: 0.5rem;
  border-left: 4px solid ${(props) => {
    switch (props.$status) {
      case "pending":
        return "#ffc107";
      case "confirmed":
        return "#17a2b8";
      case "preparing":
        return "#dc3545";
      case "out_for_delivery":
        return "#28a745";
      case "delivered":
        return "#6c757d";
      case "cancelled":
        return "#dc3545";
      default:
        return "#636e72";
    }
  }};

  &:last-child {
    margin-bottom: 0;
  }
`;

const StatusHistoryStatus = styled.span`
  font-weight: 600;
  color: #2d3436;
  text-transform: uppercase;
  font-size: 0.9rem;
`;

const StatusHistoryMeta = styled.div`
  font-size: 0.85rem;
  color: #636e72;
  margin-top: 0.25rem;
`;

const StatusHistoryNotes = styled.p`
  font-size: 0.85rem;
  color: #2d5016;
  margin-top: 0.5rem;
  font-style: italic;
`;

const VendorOrders = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("");
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const queryClient = useQueryClient();

  // Fetch orders data
  const {
    data: ordersData,
    isLoading,
    error,
    refetch,
  } = useQuery(
    "vendor-orders",
    () =>
      ordersAPI.getVendorOrders({ status: statusFilter === "all" ? undefined : statusFilter, date: dateFilter }),
    {
      keepPreviousData: true,
      onError: (error) => {
        console.error("Error fetching vendor orders:", error);
        console.error("Error response:", error.response);
      },
    }
  );

  const orders = ordersData?.data?.data?.orders || [];

  // Update order status mutation
  const updateStatusMutation = useMutation(
    async ({ orderId, status }) => {
      const response = await fetch(`/api/v1/orders/${orderId}/status`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({ status }),
      });
      if (!response.ok) throw new Error("Failed to update status");
      return response.json();
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries("vendor-orders");
        toast.success("Order status updated successfully!");
      },
      onError: (error) => {
        toast.error("Failed to update order status");
        console.error("Update status error:", error);
      },
    }
  );

  // Filter orders based on search
  const filteredOrders = orders.filter((order) => {
    const matchesSearch =
      order.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.meal_plan_title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.meal_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.standalone_meal_name?.toLowerCase().includes(searchTerm.toLowerCase());

    return matchesSearch;
  });

  // Calculate statistics
  const stats = {
    total: orders.length,
    pending: orders.filter((o) => o.status === "pending").length,
    confirmed: orders.filter((o) => o.status === "confirmed").length,
    preparing: orders.filter((o) => o.status === "preparing").length,
    out_for_delivery: orders.filter((o) => o.status === "out_for_delivery")
      .length,
    delivered: orders.filter((o) => o.status === "delivered").length,
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatTime = (timeString) => {
    if (!timeString) return "Not set";
    return new Date(`2000-01-01T${timeString}`).toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "pending":
        return <FiClock />;
      case "confirmed":
        return <FiCheckCircle />;
      case "preparing":
        return <FiPackage />;
      case "out_for_delivery":
        return <FiTruck />;
      case "delivered":
        return <FiCheckCircle />;
      case "cancelled":
        return <FiXCircle />;
      default:
        return <FiAlertCircle />;
    }
  };

  const getNextStatus = (currentStatus) => {
    const statusFlow = {
      pending: "confirmed",
      confirmed: "preparing",
      preparing: "out_for_delivery",
      out_for_delivery: "delivered",
    };
    return statusFlow[currentStatus];
  };

  const handleStatusUpdate = (orderId, newStatus) => {
    updateStatusMutation.mutate({ orderId, status: newStatus });
  };

  // Fetch order details for modal
  const {
    data: orderDetailsData,
    isLoading: isLoadingOrderDetails,
  } = useQuery(
    ["vendor-order-details", selectedOrderId],
    () => ordersAPI.getVendorOrder(selectedOrderId),
    {
      enabled: !!selectedOrderId && showModal,
      onError: (error) => {
        console.error("Error fetching order details:", error);
        toast.error("Failed to load order details");
      },
    }
  );

  const orderDetails = orderDetailsData?.data?.data?.order;

  const handleViewDetails = (orderId) => {
    setSelectedOrderId(orderId);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedOrderId(null);
  };

  const formatFullDate = (dateString) => {
    if (!dateString) return "Not set";
    return new Date(dateString).toLocaleString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (isLoading) {
    return (
      <Container>
        <Header>
          <Title>
            <FiPackage />
            Orders
          </Title>
          <Subtitle>Manage your meal orders and deliveries</Subtitle>
        </Header>
        <LoadingSpinner />
      </Container>
    );
  }

  if (error) {
    console.error("Vendor orders error details:", error);
    console.error("Error response:", error.response);
    return (
      <Container>
        <Header>
          <Title>
            <FiPackage />
            Orders
          </Title>
          <Subtitle>Manage your meal orders and deliveries</Subtitle>
        </Header>
        <EmptyState>
          <FiAlertCircle />
          <h3>Error Loading Orders</h3>
          <p>
            {error.response?.data?.message || error.message || "Failed to load order data. Please try again."}
          </p>
          <p style={{ fontSize: "0.9rem", color: "#999", marginTop: "1rem" }}>
            Status: {error.response?.status || "Unknown"}
          </p>
        </EmptyState>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <Title>
              <FiPackage />
              Orders
            </Title>
            <Subtitle>Manage your meal orders and deliveries</Subtitle>
          </div>
          <ActionButton
            className="secondary"
            onClick={() => refetch()}
            style={{ marginTop: 0 }}
          >
            <FiRefreshCw />
            Refresh
          </ActionButton>
        </div>
      </Header>

      <StatsContainer>
        <StatCard
          $color="#2d5016"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <StatHeader>
            <StatIcon $bgColor="#f0f8f0" $color="#2d5016">
              <FiPackage />
            </StatIcon>
            <StatLabel>Total Orders</StatLabel>
          </StatHeader>
          <StatValue>{stats.total}</StatValue>
        </StatCard>

        <StatCard
          $color="#ffc107"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <StatHeader>
            <StatIcon $bgColor="#fff3cd" $color="#ffc107">
              <FiClock />
            </StatIcon>
            <StatLabel>Pending</StatLabel>
          </StatHeader>
          <StatValue>{stats.pending}</StatValue>
        </StatCard>

        <StatCard
          $color="#17a2b8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <StatHeader>
            <StatIcon $bgColor="#d1ecf1" $color="#17a2b8">
              <FiCheckCircle />
            </StatIcon>
            <StatLabel>Confirmed</StatLabel>
          </StatHeader>
          <StatValue>{stats.confirmed}</StatValue>
        </StatCard>

        <StatCard
          $color="#dc3545"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <StatHeader>
            <StatIcon $bgColor="#f8d7da" $color="#dc3545">
              <FiPackage />
            </StatIcon>
            <StatLabel>Preparing</StatLabel>
          </StatHeader>
          <StatValue>{stats.preparing}</StatValue>
        </StatCard>

        <StatCard
          $color="#28a745"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <StatHeader>
            <StatIcon $bgColor="#d4edda" $color="#28a745">
              <FiTruck />
            </StatIcon>
            <StatLabel>Out for Delivery</StatLabel>
          </StatHeader>
          <StatValue>{stats.out_for_delivery}</StatValue>
        </StatCard>

        <StatCard
          $color="#6c757d"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <StatHeader>
            <StatIcon $bgColor="#e2e3e5" $color="#6c757d">
              <FiCheckCircle />
            </StatIcon>
            <StatLabel>Delivered</StatLabel>
          </StatHeader>
          <StatValue>{stats.delivered}</StatValue>
        </StatCard>
      </StatsContainer>

      <FiltersContainer>
        <SearchInput
          type="text"
          placeholder="Search orders by customer name, meal plan, or meal..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <FilterSelect
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="all">All Status</option>
          <option value="pending">Pending</option>
          <option value="confirmed">Confirmed</option>
          <option value="preparing">Preparing</option>
          <option value="out_for_delivery">Out for Delivery</option>
          <option value="delivered">Delivered</option>
          <option value="cancelled">Cancelled</option>
        </FilterSelect>
        <DateInput
          type="date"
          value={dateFilter}
          onChange={(e) => setDateFilter(e.target.value)}
          placeholder="Filter by date"
        />
      </FiltersContainer>

      {filteredOrders.length === 0 ? (
        <EmptyState>
          <FiPackage />
          <h3>No Orders Found</h3>
          <p>
            {searchTerm || statusFilter !== "all" || dateFilter
              ? "No orders match your current filters."
              : "You don't have any orders yet. Orders will appear here when customers purchase your meal plans!"}
          </p>
        </EmptyState>
      ) : (
        <OrdersGrid>
          {filteredOrders.map((order, index) => (
            <OrderCard
              key={order.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              whileHover={{ y: -5 }}
            >
              <OrderHeader>
                <OrderInfo>
                  <OrderId>Order #{order.id}</OrderId>
                  <CustomerName>
                    <FiUser />
                    {order.first_name} {order.last_name}
                  </CustomerName>
                  <CustomerPhone>
                    <FiPhone />
                    {order.customer_phone || order.user_phone || "No phone"}
                  </CustomerPhone>
                </OrderInfo>
                <StatusBadge className={order.status}>
                  {getStatusIcon(order.status)}
                  {order.status.replace("_", " ")}
                </StatusBadge>
              </OrderHeader>

              <MealInfo>
                <MealTitle>
                  <FiPackage />
                  {order.meal_plan_title || order.standalone_meal_name || "Meal Order"}
                </MealTitle>
                <MealDetails>
                  <DetailItem>
                    <FiCalendar />
                    {order.meal_name || order.standalone_meal_name || "Daily Meal"}
                  </DetailItem>
                  <DetailItem>
                    <FiClock />
                    {formatDate(order.delivery_date)}
                  </DetailItem>
                </MealDetails>
              </MealInfo>

              {/* Fulfillment Method Badge */}
              {order.fulfillment_method && (
                <div
                  style={{
                    marginBottom: "1rem",
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                  }}
                >
                  {order.fulfillment_method === "pickup" ? (
                    <>
                      <FiNavigation
                        style={{
                          color: "#e65100",
                          fontSize: "1rem",
                        }}
                      />
                      <span
                        style={{
                          padding: "0.25rem 0.75rem",
                          borderRadius: "20px",
                          fontSize: "0.75rem",
                          fontWeight: "600",
                          textTransform: "uppercase",
                          background: "#fff3e0",
                          color: "#e65100",
                          border: "1px solid #e65100",
                        }}
                      >
                        Pickup
                      </span>
                    </>
                  ) : (
                    <>
                      <FiTruck
                        style={{
                          color: "#2d5016",
                          fontSize: "1rem",
                        }}
                      />
                      <span
                        style={{
                          padding: "0.25rem 0.75rem",
                          borderRadius: "20px",
                          fontSize: "0.75rem",
                          fontWeight: "600",
                          textTransform: "uppercase",
                          background: "#e8f5e8",
                          color: "#2d5016",
                          border: "1px solid #2d5016",
                        }}
                      >
                        Delivery
                      </span>
                    </>
                  )}
                </div>
              )}

              {/* Delivery or Pickup Information */}
              {order.fulfillment_method === "pickup" ? (
                <DeliveryInfo>
                  <DeliveryTitle>
                    <FiNavigation />
                    Pickup Location
                  </DeliveryTitle>
                  <DeliveryText>
                    {order.pickup_address || "Pickup at vendor location"}
                  </DeliveryText>
                  {order.pickup_instructions && (
                    <p
                      style={{
                        color: "#2d5016",
                        fontSize: "0.85rem",
                        marginTop: "0.5rem",
                        fontStyle: "italic",
                      }}
                    >
                      {order.pickup_instructions}
                    </p>
                  )}
                  <DeliveryTime>
                    <FiClock />
                    {order.estimated_delivery_time
                      ? `Est. Pickup: ${formatTime(order.estimated_delivery_time)}`
                      : "Pickup time not set"}
                  </DeliveryTime>
                </DeliveryInfo>
              ) : (
                <DeliveryInfo>
                  <DeliveryTitle>
                    <FiMapPin />
                    Delivery Address
                  </DeliveryTitle>
                  <DeliveryText>{order.delivery_address}</DeliveryText>
                  <DeliveryTime>
                    <FiClock />
                    {order.estimated_delivery_time
                      ? `Est: ${formatTime(order.estimated_delivery_time)}`
                      : "Time not set"}
                  </DeliveryTime>
                </DeliveryInfo>
              )}

              {order.delivery_instructions && (
                <div style={{ marginBottom: "1rem" }}>
                  <h5
                    style={{
                      color: "#2d3436",
                      fontSize: "0.9rem",
                      marginBottom: "0.5rem",
                    }}
                  >
                    Special Instructions:
                  </h5>
                  <p
                    style={{
                      color: "#636e72",
                      fontSize: "0.9rem",
                      lineHeight: "1.4",
                    }}
                  >
                    {order.delivery_instructions}
                  </p>
                </div>
              )}

              <OrderActions>
                {getNextStatus(order.status) && (
                  <ActionButton
                    className="success"
                    onClick={() =>
                      handleStatusUpdate(order.id, getNextStatus(order.status))
                    }
                    disabled={updateStatusMutation.isLoading}
                  >
                    <FiRefreshCw />
                    Mark as {getNextStatus(order.status).replace("_", " ")}
                  </ActionButton>
                )}
                <ActionButton
                  className="secondary"
                  onClick={() => handleViewDetails(order.id)}
                >
                  <FiEye />
                  View Details
                </ActionButton>
              </OrderActions>
            </OrderCard>
          ))}
        </OrdersGrid>
      )}

      {/* Order Details Modal */}
      {showModal && (
        <ModalOverlay
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={handleCloseModal}
        >
          <ModalContent
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            <ModalHeader>
              <h2>
                <FiPackage />
                Order Details
              </h2>
              <button onClick={handleCloseModal}>
                <FiX />
              </button>
            </ModalHeader>
            <ModalBody>
              {isLoadingOrderDetails ? (
                <div style={{ textAlign: "center", padding: "2rem" }}>
                  <LoadingSpinner />
                </div>
              ) : orderDetails ? (
                <>
                  {/* Order Information */}
                  <DetailSection>
                    <SectionTitle>
                      <FiHash />
                      Order Information
                    </SectionTitle>
                    <DetailRow>
                      <DetailLabel>Order ID</DetailLabel>
                      <DetailValue>#{orderDetails.id}</DetailValue>
                    </DetailRow>
                    <DetailRow>
                      <DetailLabel>
                        <FiCalendar />
                        Order Date
                      </DetailLabel>
                      <DetailValue>
                        {formatFullDate(orderDetails.order_date || orderDetails.created_at)}
                      </DetailValue>
                    </DetailRow>
                    <DetailRow>
                      <DetailLabel>
                        <FiCalendar />
                        Delivery Date
                      </DetailLabel>
                      <DetailValue>
                        {formatDate(orderDetails.delivery_date)}
                      </DetailValue>
                    </DetailRow>
                    <DetailRow>
                      <DetailLabel>
                        <StatusBadge className={orderDetails.status}>
                          {getStatusIcon(orderDetails.status)}
                          {orderDetails.status.replace("_", " ")}
                        </StatusBadge>
                      </DetailLabel>
                    </DetailRow>
                    {orderDetails.tracking_number && (
                      <DetailRow>
                        <DetailLabel>
                          <FiHash />
                          Tracking Number
                        </DetailLabel>
                        <DetailValue>{orderDetails.tracking_number}</DetailValue>
                      </DetailRow>
                    )}
                  </DetailSection>

                  {/* Customer Information */}
                  <DetailSection>
                    <SectionTitle>
                      <FiUser />
                      Customer Information
                    </SectionTitle>
                    <DetailRow>
                      <DetailLabel>Name</DetailLabel>
                      <DetailValue>
                        {orderDetails.first_name} {orderDetails.last_name}
                      </DetailValue>
                    </DetailRow>
                    <DetailRow>
                      <DetailLabel>
                        <FiPhone />
                        Phone
                      </DetailLabel>
                      <DetailValue>
                        {orderDetails.customer_phone || orderDetails.user_phone || "Not provided"}
                      </DetailValue>
                    </DetailRow>
                    {orderDetails.email && (
                      <DetailRow>
                        <DetailLabel>
                          <FiMail />
                          Email
                        </DetailLabel>
                        <DetailValue>{orderDetails.email}</DetailValue>
                      </DetailRow>
                    )}
                  </DetailSection>

                  {/* Meal Information */}
                  <DetailSection>
                    <SectionTitle>
                      <FiPackage />
                      Meal Information
                    </SectionTitle>
                    {orderDetails.meal_plan_title ? (
                      <>
                        <DetailRow>
                          <DetailLabel>Meal Plan</DetailLabel>
                          <DetailValue>{orderDetails.meal_plan_title}</DetailValue>
                        </DetailRow>
                        {orderDetails.meal_name && (
                          <DetailRow>
                            <DetailLabel>Meal</DetailLabel>
                            <DetailValue>{orderDetails.meal_name}</DetailValue>
                          </DetailRow>
                        )}
                        {orderDetails.meal_description && (
                          <DetailRow>
                            <DetailLabel>Description</DetailLabel>
                            <DetailValue>{orderDetails.meal_description}</DetailValue>
                          </DetailRow>
                        )}
                      </>
                    ) : (
                      <>
                        {orderDetails.standalone_meal_name && (
                          <DetailRow>
                            <DetailLabel>Meal</DetailLabel>
                            <DetailValue>{orderDetails.standalone_meal_name}</DetailValue>
                          </DetailRow>
                        )}
                        {orderDetails.standalone_meal_description && (
                          <DetailRow>
                            <DetailLabel>Description</DetailLabel>
                            <DetailValue>{orderDetails.standalone_meal_description}</DetailValue>
                          </DetailRow>
                        )}
                      </>
                    )}
                  </DetailSection>

                  {/* Fulfillment Information */}
                  <DetailSection>
                    <SectionTitle>
                      {orderDetails.fulfillment_method === "pickup" ? (
                        <FiNavigation />
                      ) : (
                        <FiTruck />
                      )}
                      {orderDetails.fulfillment_method === "pickup"
                        ? "Pickup Information"
                        : "Delivery Information"}
                    </SectionTitle>
                    {orderDetails.fulfillment_method === "pickup" ? (
                      <>
                        <DetailRow>
                          <DetailLabel>
                            <FiMapPin />
                            Pickup Address
                          </DetailLabel>
                          <DetailValue>
                            {orderDetails.pickup_address || "Vendor location"}
                          </DetailValue>
                        </DetailRow>
                        {orderDetails.pickup_instructions && (
                          <DetailRow>
                            <DetailLabel>Pickup Instructions</DetailLabel>
                            <DetailValue>{orderDetails.pickup_instructions}</DetailValue>
                          </DetailRow>
                        )}
                      </>
                    ) : (
                      <>
                        <DetailRow>
                          <DetailLabel>
                            <FiMapPin />
                            Delivery Address
                          </DetailLabel>
                          <DetailValue>{orderDetails.delivery_address}</DetailValue>
                        </DetailRow>
                        {orderDetails.delivery_instructions && (
                          <DetailRow>
                            <DetailLabel>Delivery Instructions</DetailLabel>
                            <DetailValue>{orderDetails.delivery_instructions}</DetailValue>
                          </DetailRow>
                        )}
                      </>
                    )}
                    {orderDetails.estimated_delivery_time && (
                      <DetailRow>
                        <DetailLabel>
                          <FiClock />
                          Estimated Time
                        </DetailLabel>
                        <DetailValue>
                          {formatTime(orderDetails.estimated_delivery_time)}
                        </DetailValue>
                      </DetailRow>
                    )}
                    {orderDetails.actual_delivery_time && (
                      <DetailRow>
                        <DetailLabel>
                          <FiCheckCircle />
                          Actual Delivery Time
                        </DetailLabel>
                        <DetailValue>
                          {formatFullDate(orderDetails.actual_delivery_time)}
                        </DetailValue>
                      </DetailRow>
                    )}
                  </DetailSection>

                  {/* Special Instructions */}
                  {orderDetails.special_instructions && (
                    <DetailSection>
                      <SectionTitle>
                        <FiAlertCircle />
                        Special Instructions
                      </SectionTitle>
                      <p style={{ color: "#636e72", lineHeight: 1.6 }}>
                        {orderDetails.special_instructions}
                      </p>
                    </DetailSection>
                  )}

                  {/* Status History */}
                  {orderDetails.status_history && orderDetails.status_history.length > 0 && (
                    <DetailSection>
                      <SectionTitle>
                        <FiClock />
                        Status History
                      </SectionTitle>
                      {orderDetails.status_history.map((history, index) => (
                        <StatusHistoryItem key={index} $status={history.status}>
                          <StatusHistoryStatus>
                            {history.status.replace("_", " ").toUpperCase()}
                          </StatusHistoryStatus>
                          <StatusHistoryMeta>
                            {formatFullDate(history.created_at)}
                            {history.first_name && history.last_name && (
                              <> • Changed by {history.first_name} {history.last_name}</>
                            )}
                          </StatusHistoryMeta>
                          {history.notes && (
                            <StatusHistoryNotes>Note: {history.notes}</StatusHistoryNotes>
                          )}
                        </StatusHistoryItem>
                      ))}
                    </DetailSection>
                  )}
                </>
              ) : (
                <div style={{ textAlign: "center", padding: "2rem" }}>
                  <p>Failed to load order details</p>
                </div>
              )}
            </ModalBody>
          </ModalContent>
        </ModalOverlay>
      )}
    </Container>
  );
};

export default VendorOrders;
